import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useNavigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  LayoutDashboard, 
  ArrowDownRight, 
  ArrowUpRight,
  FileText,
  History,
  User,
  LogOut,
  Menu,
  X,
  TrendingUp,
  AlertTriangle,
  ClipboardList,
  Building2,
  Gift
} from 'lucide-react';
import { Toaster } from './ui/sonner';
import LanguageSelector from './LanguageSelector';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_sgficapital/artifacts/i5d6sap9_LOGO%20SGFI-nuevo.png';
const SESSION_TIMEOUT = 3 * 60 * 1000; // 3 minutes in milliseconds
const WARNING_TIME = 30 * 1000; // Show warning 30 seconds before timeout

export default function UserLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasNewCampaigns, setHasNewCampaigns] = useState(false);
  
  // Session timeout state
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(WARNING_TIME / 1000);
  const timeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);
  const countdownRef = useRef(null);

  // Reset session timeout
  const resetTimeout = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    
    setShowTimeoutWarning(false);
    setTimeRemaining(WARNING_TIME / 1000);

    // Set warning timeout (shows warning 30s before session expires)
    warningTimeoutRef.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setTimeRemaining(WARNING_TIME / 1000);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_TIME);

    // Set actual timeout
    timeoutRef.current = setTimeout(() => {
      handleSessionExpired();
    }, SESSION_TIMEOUT);
  }, []);

  // Handle session expired
  const handleSessionExpired = useCallback(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // Extend session
  const extendSession = () => {
    resetTimeout();
  };

  // Handle logout
  const handleLogout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    logout();
    navigate('/');
  };

  // Initialize timeout
  useEffect(() => {
    resetTimeout();

    // Activity listeners
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    const handleActivity = () => {
      if (!showTimeoutWarning) {
        resetTimeout();
      }
    };

    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity);
    });

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [resetTimeout, showTimeoutWarning]);

  // Check for new campaigns
  useEffect(() => {
    const checkNewCampaigns = async () => {
      try {
        const response = await axios.get(`${API_URL}/campaigns/has-new`);
        setHasNewCampaigns(response.data.has_new);
      } catch (error) {
        // Silently fail
      }
    };
    
    checkNewCampaigns();
    // Check every 60 seconds
    const interval = setInterval(checkNewCampaigns, 60000);
    return () => clearInterval(interval);
  }, []);

  // Clear notification when visiting campaigns page
  useEffect(() => {
    if (location.pathname === '/campaigns') {
      setHasNewCampaigns(false);
    }
  }, [location.pathname]);

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/campaigns', icon: Gift, label: t('campaigns.title'), hasNotification: hasNewCampaigns },
    { to: '/deposit', icon: ArrowDownRight, label: t('nav.deposits') },
    { to: '/withdraw', icon: ArrowUpRight, label: t('nav.withdrawals') },
    { to: '/deposits', icon: FileText, label: t('deposits.title') },
    { to: '/withdrawals', icon: History, label: t('withdrawals.title') },
    { to: '/statement', icon: ClipboardList, label: t('statement.title') },
    { to: '/rendimentos', icon: TrendingUp, label: t('nav.rendimentos') },
    // TEMPORALMENTE DESACTIVADO: { to: '/fund-growth', icon: Building2, label: t('fundGrowth.title') },
    { to: '/profile', icon: User, label: t('nav.profile') },
  ];

  // Bottom nav - solo 4 items principales
  const bottomNavItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/deposit', icon: ArrowDownRight, label: t('nav.deposits') },
    { to: '/withdraw', icon: ArrowUpRight, label: t('nav.withdrawals') },
    { to: '/profile', icon: User, label: t('nav.profile') },
  ];

  return (
    <div className="min-h-screen bg-[#0d1218]" data-testid="user-layout">
      <Toaster richColors position="top-center" />
      
      {/* Session Timeout Warning Modal */}
      <Dialog open={showTimeoutWarning} onOpenChange={() => {}}>
        <DialogContent className="bg-[#1c262d] border-white/10 max-w-md" data-testid="session-timeout-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              {t('auth.sessionWarning')}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[#93a5af] mb-4">
              {t('auth.sessionExpired')}
            </p>
            
            {/* Countdown Timer */}
            <div className="bg-[#0d1218] rounded-xl p-4 mb-4">
              <div className="text-center">
                <span className="text-4xl font-bold text-amber-400">{timeRemaining}</span>
                <span className="text-[#93a5af] ml-2">{t('time.seconds', 'seconds')}</span>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-3 h-2 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-amber-500 transition-all duration-1000 ease-linear"
                  style={{ width: `${(timeRemaining / (WARNING_TIME / 1000)) * 100}%` }}
                />
              </div>
            </div>
            
            <p className="text-[#93a5af]/70 text-sm text-center mb-4">
              {t('auth.stayLoggedIn')}
            </p>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex-1 border-white/10 text-[#93a5af] hover:bg-white/5"
              data-testid="logout-session-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('common.logout')}
            </Button>
            <Button
              onClick={extendSession}
              className="flex-1 bg-[#2e8036] hover:bg-[#247029]"
              data-testid="extend-session-btn"
            >
              {t('common.continue')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Sidebar - Desktop */}
      <aside className="fixed left-0 top-0 h-screen w-64 bg-[#1c262d] border-r border-white/5 hidden lg:flex flex-col z-40">
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <NavLink to="/" className="block hover:opacity-80 transition-opacity">
            <img src={LOGO_URL} alt="SGFI Capital" className="h-10 w-auto" />
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${
                  isActive
                    ? 'bg-[#2e8036]/20 text-[#2e8036]'
                    : 'text-[#93a5af] hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {item.hasNotification && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-emerald-500 rounded-full animate-pulse" />
              )}
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/5">
          <LanguageSelector className="mb-3" />
          <div className="flex items-center gap-3 p-3 bg-white/5 rounded-xl mb-3">
            <div className="w-10 h-10 rounded-full bg-[#2e8036]/10 flex items-center justify-center">
              <User className="w-5 h-5 text-[#2e8036]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name}</p>
              <p className="text-xs text-[#93a5af] truncate">{user?.email}</p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('common.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#1c262d]/95 backdrop-blur-lg border-b border-white/5 flex items-center justify-between px-4 z-50 safe-area-top">
        <img src={LOGO_URL} alt="SGFI Capital" className="h-7 w-auto" />
        <div className="flex items-center gap-2">
          <LanguageSelector />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center active:scale-95 transition-transform"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-white" /> : <Menu className="w-5 h-5 text-white" />}
          </button>
        </div>
      </header>

      {/* Mobile Full Screen Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-[#0d1218] z-40 pt-14 safe-area-top overflow-y-auto">
          <div className="p-4">
            {/* User Card */}
            <div className="flex items-center gap-3 p-4 bg-white/5 rounded-2xl mb-6">
              <div className="w-12 h-12 rounded-full bg-[#2e8036]/10 flex items-center justify-center">
                <User className="w-6 h-6 text-[#2e8036]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{user?.name}</p>
                <p className="text-sm text-[#93a5af] truncate">{user?.email}</p>
              </div>
            </div>

            {/* Navigation Links */}
            <nav className="space-y-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-4 px-4 py-4 rounded-2xl transition-all active:scale-[0.98] relative ${
                      isActive
                        ? 'bg-[#2e8036]/10 text-white border border-[#2e8036]/20'
                        : 'text-[#93a5af] hover:text-white hover:bg-white/5 active:bg-white/10'
                    }`
                  }
                >
                  <item.icon className="w-6 h-6" />
                  <span className="text-lg font-medium">{item.label}</span>
                  {item.hasNotification && (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />
                  )}
                </NavLink>
              ))}
            </nav>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-4 px-4 py-4 rounded-2xl text-red-400 hover:bg-red-500/10 active:bg-red-500/20 transition-all mt-6 active:scale-[0.98]"
            >
              <LogOut className="w-6 h-6" />
              <span className="text-lg font-medium">{t('common.logout')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-14 lg:pt-0 pb-20 lg:pb-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation - Floating Style */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 safe-area-bottom z-30">
        <div className="mx-3 mb-3 bg-[#161C22]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl">
          <div className="flex items-center justify-around py-2">
            {bottomNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 py-2 px-4 rounded-xl transition-all active:scale-95 ${
                    isActive 
                      ? 'text-blue-400 bg-blue-500/10' 
                      : 'text-slate-500 active:bg-white/5'
                  }`
                }
              >
                <item.icon className="w-6 h-6" />
                <span className="text-[11px] font-medium">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </div>
      </nav>
    </div>
  );
}
